import React, { useEffect, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { useAccount } from "../contexts/AccountContext";
import { useNavigate, Navigate, Link } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import { LogOut, GraduationCap, UserCircle, FileDown, Images, CheckCircle2, Clock, Plus } from "lucide-react";
import SiteButton from "../components/SiteButton";

export default function AccountDashboard() {
  const { lang } = useApp();
  const { account, ready, logout } = useAccount();
  const navigate = useNavigate();
  const tr = (pt, en) => (lang !== "en" ? pt : en);

  const [data, setData] = useState(null);

  useEffect(() => {
    if (account) {
      api.get("/account/enrollments").then((r) => setData(r.data)).catch(() => setData({ enrollments: [], diplomas_available: false, diploma_count: 0 }));
    }
  }, [account]);

  if (ready && !account) return <Navigate to="/account" replace />;
  if (!ready || !account) {
    return <div className="text-center pt-20 font-fredoka text-2xl text-[#0B1D3A]">{tr("A carregar...", "Loading...")}</div>;
  }

  const doLogout = () => { logout(); navigate("/account"); };
  const emailQ = encodeURIComponent(account.email);
  const pdfUrl = `${API_BASE}/diploma/teacher/pdf?email=${emailQ}&lang=${lang}`;
  const zipUrl = `${API_BASE}/diploma/teacher/png?email=${emailQ}&lang=${lang}`;
  const canDownload = data && data.diplomas_available && data.diploma_count > 0;

  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="account-dashboard">
      <div className="bg-white rounded-3xl shadow-xl border border-[#0B1D3A]/5 p-8">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
          <div className="flex items-center gap-4">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FF6B6B]/15 text-[#FF6B6B]">
              <UserCircle size={30} />
            </span>
            <div>
              <h1 className="font-fredoka text-2xl font-bold text-[#0B1D3A]">{tr("Olá", "Hi")}, {account.name || account.email}!</h1>
              <p className="text-sm text-[#0B1D3A]/60 font-nunito">
                {account.email} · {account.role === "parent" ? tr("Encarregado(a)", "Parent") : tr("Professor(a)", "Teacher")}
              </p>
            </div>
          </div>
          <button onClick={doLogout} data-testid="account-logout"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1D3A]/60 hover:text-[#FF6B6B] transition-colors">
            <LogOut size={16} /> {tr("Terminar sessão", "Sign out")}
          </button>
        </div>

        {/* My classes */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-outfit font-bold text-[#0B1D3A]">{tr("As minhas turmas", "My classes")}</h2>
          <Link to="/enroll" data-testid="account-enroll-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#005F73] hover:underline">
            <Plus size={16} /> {tr("Inscrever turma", "Enroll a class")}
          </Link>
        </div>

        {!data ? (
          <p className="text-[#0B1D3A]/50 font-nunito text-sm">{tr("A carregar turmas...", "Loading classes...")}</p>
        ) : data.enrollments.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#0B1D3A]/15 p-6 text-center" data-testid="account-no-classes">
            <GraduationCap className="mx-auto text-[#0B1D3A]/30" />
            <p className="mt-2 text-sm text-[#0B1D3A]/60 font-nunito">{tr("Ainda não inscreveu nenhuma turma.", "You haven't enrolled any class yet.")}</p>
            <SiteButton onClick={() => navigate("/enroll")} color="#1B5E3A" icon={Plus} size="sm" fullWidth={false} className="mt-3">
              {tr("Inscrever a primeira turma", "Enroll your first class")}
            </SiteButton>
          </div>
        ) : (
          <div className="space-y-3" data-testid="account-classes-list">
            {data.enrollments.map((e) => (
              <div key={e.id} className="rounded-2xl border border-[#0B1D3A]/10 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`account-class-${e.id}`}>
                <div>
                  <p className="font-outfit font-bold text-[#0B1D3A]">{e.class_name} <span className="text-[#0B1D3A]/40 font-normal text-sm">· {e.code}</span></p>
                  <p className="text-sm text-[#0B1D3A]/60 font-nunito">
                    {(lang === "en" ? e.quiz_name_en : (e.quiz_name_br || e.quiz_name_pt)) || ""} · {e.num_students} {tr("alunos", "students")}
                    {e.school ? ` · ${e.school}` : ""}
                  </p>
                </div>
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${e.payment_confirmed ? "bg-[#2D6A4F]/10 text-[#2D6A4F]" : "bg-[#FF9F1C]/15 text-[#B26A00]"}`}>
                  {e.payment_confirmed ? <CheckCircle2 size={13} /> : <Clock size={13} />}
                  {e.payment_confirmed ? tr("Pago", "Paid") : tr("Pagamento pendente", "Payment pending")}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Diplomas */}
        <h2 className="font-outfit font-bold text-[#0B1D3A] mt-8 mb-3">{tr("Diplomas da turma", "Class diplomas")}</h2>
        {data && canDownload ? (
          <div className="rounded-2xl bg-[#005F73]/[0.06] border border-[#005F73]/20 p-5" data-testid="account-diplomas">
            <p className="text-sm text-[#0B1D3A]/70 font-nunito mb-3">
              {tr(`${data.diploma_count} diploma(s) disponível(eis) para os seus alunos.`, `${data.diploma_count} diploma(s) available for your students.`)}
            </p>
            <div className="flex gap-3 flex-wrap">
              <SiteButton href={pdfUrl} download testId="account-diplomas-pdf" color="#005F73" icon={FileDown} size="sm" fullWidth={false}>
                {tr("Descarregar PDF", "Download PDF")}
              </SiteButton>
              <SiteButton href={zipUrl} download testId="account-diplomas-zip" color="#1B5E3A" icon={Images} size="sm" fullWidth={false}>
                {tr("Imagens (ZIP)", "Images (ZIP)")}
              </SiteButton>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#0B1D3A]/50 font-nunito" data-testid="account-diplomas-empty">
            {data && !data.diplomas_available
              ? tr("Os diplomas ainda não estão disponíveis.", "Diplomas are not available yet.")
              : tr("Ainda não há diplomas (os seus alunos ainda não concluíram o concurso).", "No diplomas yet (your students haven't completed the contest).")}
          </p>
        )}
      </div>
    </div>
  );
}
